import { ChannelType } from "../../cep/types/channel-type";
import { ColumnType } from "../../query/visual/types/column";
import {
  IntervalUnit,
  RelativeDirection,
} from "../../query/visual/types/interval";
import { ScheduleType } from "../../sync/scheduler/schedule-types";
import {
  JourneyNodeSyncMode,
  JourneyNodeType,
  MISSING_WAREHOUSE_DATA_BEHAVIORS,
  SYNC_TIMEOUT_ACTIONS,
} from "../types/journey-node-types";
import { JOURNEY_ENVELOPE_ERROR_CODES } from "./error-codes";
import type {
  JourneyEnvelopeErrorCode,
  JourneyEnvelopeIssueParams,
} from "./error-codes";
import { z } from "zod/v4";

const violationParams = (
  code: JourneyEnvelopeErrorCode,
): JourneyEnvelopeIssueParams => ({ code, bucket: "violations" });

type SchemaIssueContext = {
  addIssue: (issue: {
    code: "custom";
    message: string;
    params: JourneyEnvelopeIssueParams;
    path: Array<string | number>;
  }) => void;
};

const addSchemaViolation = (
  context: SchemaIssueContext,
  code: JourneyEnvelopeErrorCode,
  message: string,
  path: Array<string | number>,
) => {
  context.addIssue({
    code: "custom",
    message,
    params: violationParams(code),
    path,
  });
};

const intervalUnitSchema = z.enum(IntervalUnit);
const durationSchema = z.strictObject({
  unit: intervalUnitSchema,
  quantity: z.number().int().min(0),
});

const conditionGroupSchema = z.looseObject({
  type: z.enum(["and", "or"]),
  conditions: z.array(z.json()),
});

const exclusionsSchema = z
  .union([
    z.strictObject({}),
    z.strictObject({
      type: z.literal("any_journey"),
      journey_ids: z.array(z.string()).optional(),
    }),
    z.strictObject({
      type: z.literal("specific_journey"),
      journey_ids: z.array(z.string().uuid()).min(1),
    }),
  ])
  .optional();

const entryFields = {
  max_num_entries: z.number().int().min(-1),
  exclusions: exclusionsSchema,
  re_entry_delay: durationSchema.optional(),
};

export const entryCohortConfigSchema = z.looseObject({
  type: z.literal(JourneyNodeType.EntryCohort),
  ...entryFields,
});

const lookbackWindowSchema = z
  .strictObject({
    relative: durationSchema.nullable().optional(),
    absolute: z.iso.datetime({ precision: 3 }).nullable().optional(),
  })
  .refine((window) => !(window.relative != null && window.absolute != null), {
    message: "Lookback windows cannot define both relative and absolute values",
    params: violationParams(JOURNEY_ENVELOPE_ERROR_CODES.MalformedNodeConfig),
  });

export const entryEventConfigSchema = z.looseObject({
  type: z.literal(JourneyNodeType.EntryEvent),
  ...entryFields,
  max_simultaneous_entries: z.number().int().min(-1),
  event_conditions: conditionGroupSchema.optional(),
  segment_conditions: conditionGroupSchema.optional(),
  visual_query_conditions: conditionGroupSchema.optional(),
  lookback_window: lookbackWindowSchema.nullable().optional(),
});

const propertyDelayOffsetSchema = z
  .strictObject({
    unit: intervalUnitSchema.optional(),
    quantity: z.number().int().min(0).optional(),
    direction: z.enum(RelativeDirection).optional(),
  })
  .superRefine((offset, context) => {
    if (offset.direction !== undefined && offset.unit === undefined) {
      addSchemaViolation(
        context,
        JOURNEY_ENVELOPE_ERROR_CODES.MalformedNodeConfig,
        "Unit is required when offset direction is set",
        ["unit"],
      );
    }
    if (offset.direction !== undefined && offset.quantity === undefined) {
      addSchemaViolation(
        context,
        JOURNEY_ENVELOPE_ERROR_CODES.MalformedNodeConfig,
        "Quantity is required when offset direction is set",
        ["quantity"],
      );
    }
  });

const propertyDelaySchema = z.strictObject({
  offset: propertyDelayOffsetSchema.optional(),
  referenceProperty: z.strictObject({
    property: z.union([z.string(), z.record(z.string(), z.json())]),
    propertyType: z.enum([ColumnType.Timestamp, ColumnType.Date]),
  }),
});

const timeOfDaySchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Time must be in HH:mm 24-hour format");

export const nextMatchingTimeScheduleSchema = z.strictObject({
  gracePeriod: durationSchema.nullable().optional(),
  time: timeOfDaySchema,
  daysOfWeek: z.array(z.number().int().min(0).max(6)).optional(),
  timezone: z.string().refine(
    (timezone) => {
      try {
        Intl.DateTimeFormat(undefined, { timeZone: timezone });
        return true;
      } catch {
        return false;
      }
    },
    {
      message: "Invalid timezone",
      params: violationParams(JOURNEY_ENVELOPE_ERROR_CODES.MalformedNodeConfig),
    },
  ),
});

export const timeDelayConfigSchema = z.looseObject({
  type: z.literal(JourneyNodeType.TimeDelay),
  buffer_override_seconds: z.number().nullable().optional(),
  delay: durationSchema.optional(),
  propertyDelay: propertyDelaySchema.optional(),
  absolute: z.iso.datetime({ precision: 3 }).optional(),
  nextMatchingTime: nextMatchingTimeScheduleSchema.optional(),
});

export const waitUntilEventConfigSchema = z.looseObject({
  type: z.literal(JourneyNodeType.WaitUntilEvent),
  timeout_duration: durationSchema,
  buffer_override_seconds: z.number().nullable().optional(),
  event_conditions: conditionGroupSchema.optional(),
  segment_conditions: conditionGroupSchema.optional(),
});

export const segmentsConfigSchema = z.looseObject({
  type: z.literal(JourneyNodeType.Segments),
  on_missing_warehouse_data: z
    .enum(MISSING_WAREHOUSE_DATA_BEHAVIORS)
    .optional(),
});

export const segmentBranchConfigSchema = z.looseObject({
  type: z.literal(JourneyNodeType.SegmentBranch),
  segment_priority_rank: z.number().int(),
  segment_is_catch_all: z.boolean(),
  segment_conditions: conditionGroupSchema,
});

export const syncNodeConfigSchema = z.looseObject({
  type: z.literal(JourneyNodeType.Sync),
});

export const waitUntilEventBranchConfigSchema = z.looseObject({
  type: z.literal(JourneyNodeType.WaitUntilEventBranch),
  branch: z.enum(["event", "timeout"]),
});

export const splitsConfigSchema = z.looseObject({
  type: z.literal(JourneyNodeType.Splits),
  salt_override: z.string().nullable().optional(),
});

export const splitBranchConfigSchema = z.looseObject({
  type: z.literal(JourneyNodeType.SplitBranch),
  percentage: z.number().int().gt(0).lt(100),
});

export const messageNodeConfigSchema = z.looseObject({
  type: z.literal(JourneyNodeType.Message),
  message_type: z.enum(ChannelType),
});

const contextValueSchema = z.discriminatedUnion("variable_type", [
  z.strictObject({
    variable_name: z.string().regex(/^[a-z0-9_]+$/),
    variable_type: z.literal("STRING"),
    value: z.string().min(1, "Value is a required field"),
  }),
  z.strictObject({
    variable_name: z.string().regex(/^[a-z0-9_]+$/),
    variable_type: z.literal("NUMBER"),
    value: z.number(),
  }),
  z.strictObject({
    variable_name: z.string().regex(/^[a-z0-9_]+$/),
    variable_type: z.literal("BOOLEAN"),
    value: z.boolean(),
  }),
]);

export const contextConfigSchema = z.looseObject({
  type: z.literal(JourneyNodeType.Context),
  values: z.array(contextValueSchema),
});

const contextVariableTypeSchema = z.enum([
  "STRING",
  "NUMBER",
  "BOOLEAN",
  "TIMESTAMP",
  "JSON",
]);

const couponAdditionalColumnSchema = z.strictObject({
  source_column: z.string().min(1),
  context_variable_name: z.string().min(1),
  context_variable_type: contextVariableTypeSchema,
});

export const decisionConfigSchema = z.looseObject({
  type: z.literal(JourneyNodeType.Decision),
  attribution: z.looseObject({
    window: z.looseObject({
      unit: intervalUnitSchema,
      value: z.number().int().gt(0),
    }),
  }),
});

export const couponAssignmentConfigSchema = z
  .looseObject({
    type: z.literal(JourneyNodeType.CouponAssignment),
    pool_model_id: z.string(),
    assignment_column: z.string(),
    assignment_variable_name: z.string(),
    additional_columns: z.array(couponAdditionalColumnSchema).optional(),
    filter: conditionGroupSchema.optional(),
  })
  .superRefine((config, context) => {
    if (config.pool_model_id.length === 0) {
      return;
    }
    if (config.assignment_column.length === 0) {
      addSchemaViolation(
        context,
        JOURNEY_ENVELOPE_ERROR_CODES.MalformedNodeConfig,
        "Assignment column is required when a pool model is set",
        ["assignment_column"],
      );
    }
    if (config.assignment_variable_name.length === 0) {
      addSchemaViolation(
        context,
        JOURNEY_ENVELOPE_ERROR_CODES.MalformedNodeConfig,
        "Assignment variable name is required when a pool model is set",
        ["assignment_variable_name"],
      );
    }
  });

export const journeyNodeConfigSchema = z.discriminatedUnion("type", [
  entryCohortConfigSchema,
  entryEventConfigSchema,
  timeDelayConfigSchema,
  waitUntilEventConfigSchema,
  segmentsConfigSchema,
  segmentBranchConfigSchema,
  syncNodeConfigSchema,
  waitUntilEventBranchConfigSchema,
  splitsConfigSchema,
  splitBranchConfigSchema,
  messageNodeConfigSchema,
  contextConfigSchema,
  couponAssignmentConfigSchema,
  decisionConfigSchema,
]);

const syncExitDurationSchema = z.strictObject({
  unit: intervalUnitSchema,
  quantity: z.number().int().positive(),
  buffer_override_seconds: z.number().optional(),
});

const flooredDurationSchema = z
  .strictObject({
    unit: z.enum([
      IntervalUnit.Hour,
      IntervalUnit.Day,
      IntervalUnit.Week,
      IntervalUnit.Month,
      IntervalUnit.Year,
    ]),
    quantity: z.number().int().positive(),
  })
  .refine(
    (duration) => duration.unit !== IntervalUnit.Hour || duration.quantity >= 4,
    {
      message: "Hourly durations must be at least 4 hours",
      params: violationParams(JOURNEY_ENVELOPE_ERROR_CODES.MalformedSidecar),
    },
  );

export const journeySyncExitConfigSchema = z.strictObject({
  remove_after: syncExitDurationSchema.optional(),
  remove_on_journey_exit: z.boolean().optional(),
  expire_after: flooredDurationSchema.optional(),
  wait_for: flooredDurationSchema.optional(),
  on_timeout: z.enum(SYNC_TIMEOUT_ACTIONS).optional(),
  send_in_run: z.boolean().optional(),
});

export const placeholderSyncConfigSchema = z.strictObject({
  placeholder_destination_id: z.string().min(1),
});

const EXIT_CONFIG_FIELDS_BY_MODE: Record<
  JourneyNodeSyncMode,
  ReadonlyArray<keyof z.infer<typeof journeySyncExitConfigSchema>>
> = {
  [JourneyNodeSyncMode.Cohort]: ["remove_after", "remove_on_journey_exit"],
  [JourneyNodeSyncMode.Trigger]: ["expire_after"],
  [JourneyNodeSyncMode.Message]: ["wait_for", "on_timeout", "send_in_run"],
  // Members without a result follow the Set variable tile's on_missing_value.
  [JourneyNodeSyncMode.Lookup]: [],
};

const SYNC_MODE_LABELS: Record<JourneyNodeSyncMode, string> = {
  [JourneyNodeSyncMode.Cohort]: "Cohort",
  [JourneyNodeSyncMode.Trigger]: "Trigger",
  [JourneyNodeSyncMode.Message]: "Message",
  [JourneyNodeSyncMode.Lookup]: "Lookup",
};

export const configuredSyncConfigSchema = z
  .strictObject({
    destination_instance_id: z.string().min(1),
    mode: z.enum(JourneyNodeSyncMode),
    exit_config: journeySyncExitConfigSchema,
  })
  .superRefine((config, context) => {
    if (
      config.mode === JourneyNodeSyncMode.Message &&
      config.exit_config.wait_for === undefined
    ) {
      addSchemaViolation(
        context,
        JOURNEY_ENVELOPE_ERROR_CODES.InvalidSyncConfig,
        "Message syncs require wait_for",
        ["exit_config", "wait_for"],
      );
    }
    const allowedFields = EXIT_CONFIG_FIELDS_BY_MODE[config.mode];
    for (const field of Object.keys(config.exit_config) as Array<
      keyof typeof config.exit_config
    >) {
      if (
        config.exit_config[field] !== undefined &&
        !allowedFields.includes(field)
      ) {
        addSchemaViolation(
          context,
          JOURNEY_ENVELOPE_ERROR_CODES.SyncExitModeMismatch,
          `${SYNC_MODE_LABELS[config.mode]} syncs cannot define ${field}`,
          ["exit_config", field],
        );
      }
    }
  });

export const journeySyncConfigSchema = z.union([
  placeholderSyncConfigSchema,
  configuredSyncConfigSchema,
]);

export const orphanedSyncConfigSchema = z.strictObject({
  orphaned: z.literal(true),
  mode: z.string(),
  exit_config: z.unknown().refine((value) => value !== undefined),
});

export const readJourneySyncConfigSchema = z.union([
  journeySyncConfigSchema,
  orphanedSyncConfigSchema,
]);

export const journeyMessageConfigSchema = z.strictObject({
  message_id: z.string().min(1),
  channel_type: z.enum(ChannelType),
});

const positionSchema = z.strictObject({
  x: z.number(),
  y: z.number(),
});

export const journeyNoteSchema = z
  .strictObject({
    id: z.string().uuid().optional(),
    content: z.string(),
    node_id: z.string().nullable().optional(),
    position: positionSchema.nullable().optional(),
  })
  .superRefine((note, context) => {
    const hasNodeTarget = typeof note.node_id === "string";
    const hasPositionTarget = note.position != null;
    if (hasNodeTarget === hasPositionTarget) {
      addSchemaViolation(
        context,
        JOURNEY_ENVELOPE_ERROR_CODES.NoteTargetXor,
        "Notes must have exactly one of node_id or position",
        [],
      );
    }
  });

const intervalScheduleSchema = z.strictObject({
  type: z.literal(ScheduleType.Interval),
  schedule: z.strictObject({
    interval: z.strictObject({
      unit: z.enum(["minute", "hour", "day", "week"]),
      quantity: z.number().int().positive(),
    }),
  }),
  startDate: z.string().nullable().optional(),
  endDate: z.string().nullable().optional(),
  endMode: z.enum(["drain", "hard_stop", "pause"]).nullable().optional(),
});

const cronScheduleSchema = z.strictObject({
  type: z.literal(ScheduleType.Cron),
  schedule: z.strictObject({ expression: z.string().min(1) }),
  startDate: z.string().nullable().optional(),
  endDate: z.string().nullable().optional(),
  endMode: z.enum(["drain", "hard_stop", "pause"]).nullable().optional(),
});

const daysSchema = z.strictObject({
  monday: z.boolean().optional(),
  tuesday: z.boolean().optional(),
  wednesday: z.boolean().optional(),
  thursday: z.boolean().optional(),
  friday: z.boolean().optional(),
  saturday: z.boolean().optional(),
  sunday: z.boolean().optional(),
});

const visualCronScheduleSchema = z.strictObject({
  type: z.literal(ScheduleType.VisualCron),
  schedule: z.strictObject({
    expressions: z.array(
      z.strictObject({
        time: z.string(),
        days: daysSchema,
      }),
    ),
  }),
  startDate: z.string().nullable().optional(),
  endDate: z.string().nullable().optional(),
  endMode: z.enum(["drain", "hard_stop", "pause"]).nullable().optional(),
});

export const journeyScheduleSchema = z.union([
  intervalScheduleSchema,
  cronScheduleSchema,
  visualCronScheduleSchema,
]);

export const journeyExitCriteriaSchema = conditionGroupSchema;

export const journeyContextConfigSchema = z.strictObject({
  variables: z.array(
    z.strictObject({
      variable_name: z.string().regex(/^[a-z0-9_]+$/),
      variable_type: contextVariableTypeSchema,
    }),
  ),
});

export const journeyEnvelopeJourneySchema = z.strictObject({
  id: z.string().min(1).optional(),
  parent_model_id: z.string().min(1),
  name: z.string(),
  description: z.string().nullable(),
  status: z.literal("disabled", {
    error: `${JOURNEY_ENVELOPE_ERROR_CODES.InvalidAuthoringStatus}: Authoring envelopes must have status disabled`,
  }),
  schedule: journeyScheduleSchema.optional(),
  exit_criteria: journeyExitCriteriaSchema.nullable(),
  context_config: journeyContextConfigSchema.nullable(),
  notes: z.array(journeyNoteSchema),
});

const supportedJourneyNodeTypes = [
  JourneyNodeType.EntryEvent,
  JourneyNodeType.EntryCohort,
  JourneyNodeType.WaitUntilEvent,
  JourneyNodeType.TimeDelay,
  JourneyNodeType.Segments,
  JourneyNodeType.Splits,
  JourneyNodeType.Sync,
  JourneyNodeType.Context,
  JourneyNodeType.CouponAssignment,
  JourneyNodeType.Message,
  JourneyNodeType.SegmentBranch,
  JourneyNodeType.WaitUntilEventBranch,
  JourneyNodeType.SplitBranch,
  JourneyNodeType.Decision,
] as const;

const deferredJourneyNodeTypes = [
  JourneyNodeType.NoOp,
  JourneyNodeType.EntryAudienceExperiment,
  JourneyNodeType.EntryAudienceExperimentBranch,
  JourneyNodeType.Function,
  JourneyNodeType.EventEmission,
] as const;

type CoveredJourneyNodeType =
  | (typeof supportedJourneyNodeTypes)[number]
  | (typeof deferredJourneyNodeTypes)[number];

const assertJourneyNodeTypesExhaustive = (
  _uncoveredTypes: Record<
    Exclude<JourneyNodeType, CoveredJourneyNodeType>,
    never
  >,
) => {};
assertJourneyNodeTypesExhaustive({});

export const journeyEnvelopeNodeSchema = z.strictObject({
  id: z.string().min(1),
  type: z.enum(supportedJourneyNodeTypes, {
    error: `${JOURNEY_ENVELOPE_ERROR_CODES.UnsupportedNodeType}: Node type is outside the authoring envelope union`,
  }),
  name: z.string().min(1),
  config: journeyNodeConfigSchema,
  segment_id: z.string().min(1).nullable().optional(),
  event_relationship_id: z.string().min(1).nullable().optional(),
  position: positionSchema.nullable().optional(),
  description: z.string().nullable().optional(),
  sync_configs: z.array(journeySyncConfigSchema).optional(),
  message_config: journeyMessageConfigSchema.optional(),
});

export const readJourneyEnvelopeNodeSchema = journeyEnvelopeNodeSchema.extend({
  sync_configs: z.array(readJourneySyncConfigSchema).optional(),
});

export const journeyEnvelopeEdgeSchema = z.strictObject({
  from_node_id: z.string().min(1),
  to_node_id: z.string().min(1),
});

export const journeyGraphEnvelopeShape = {
  journey: journeyEnvelopeJourneySchema,
  nodes: z.array(journeyEnvelopeNodeSchema),
  edges: z.array(journeyEnvelopeEdgeSchema),
};

export const journeyGraphEnvelopeBaseSchema = z.strictObject(
  journeyGraphEnvelopeShape,
);

export const rawJourneyNodeSchema = z.looseObject({
  id: z.string().optional(),
  type: z.string().optional(),
  name: z.string().optional(),
  config: z.unknown().optional(),
  sync_configs: z.array(z.unknown()).optional(),
  message_config: z.unknown().optional(),
});

const tolerantJourneySchema = z.looseObject({
  id: z.string().optional(),
  parent_model_id: z.string().optional(),
  name: z.string().optional(),
  description: z.string().nullable().optional(),
  status: z.string().optional(),
  schedule: z.unknown().optional(),
  exit_criteria: z.unknown().optional(),
  context_config: z.unknown().optional(),
  notes: z.array(z.unknown()).optional(),
});

export const tolerantJourneyGraphEnvelopeSchema = z.looseObject({
  journey: tolerantJourneySchema,
  nodes: z.array(
    z.union([readJourneyEnvelopeNodeSchema, rawJourneyNodeSchema]),
  ),
  edges: z.array(z.unknown()),
});

export type JourneyNodeConfig = z.infer<typeof journeyNodeConfigSchema>;
export type SegmentBranchConfig = z.infer<typeof segmentBranchConfigSchema>;
export type JourneySyncConfig = z.infer<typeof journeySyncConfigSchema>;
export type JourneyMessageConfig = z.infer<typeof journeyMessageConfigSchema>;
export type JourneyEnvelopeNode = z.infer<typeof journeyEnvelopeNodeSchema>;
export type RawJourneyNode = z.infer<typeof rawJourneyNodeSchema>;
export type TolerantJourneyGraphEnvelope = z.infer<
  typeof tolerantJourneyGraphEnvelopeSchema
>;

export const JOURNEY_NODE_CHANGE_FIELDS = [
  "name",
  "type",
  "description",
  "segment_id",
  "event_relationship_id",
  "config",
  "message_config",
  "sync_configs",
] as const;

export const JOURNEY_LEVEL_CHANGE_FIELDS = [
  "name",
  "description",
  "schedule",
  "exit_criteria",
  "context_config",
  "notes",
] as const;

export const journeyChangeItemSchema = z.discriminatedUnion("kind", [
  z.strictObject({
    kind: z.literal("node"),
    change: z.enum(["added", "removed", "changed"]),
    id: z.string(),
    type: z.string(),
    name: z.string(),
    // Set for message nodes so a removed one, which the reply no longer carries, can still show its channel.
    message_type: z.enum(ChannelType).optional(),
    fields: z.array(z.enum(JOURNEY_NODE_CHANGE_FIELDS)).optional(),
    // Baseline value of each changed field; a field whose value is too large to store has no entry.
    before: z
      .partialRecord(z.enum(JOURNEY_NODE_CHANGE_FIELDS), z.json())
      .optional(),
  }),
  z.strictObject({
    kind: z.literal("edge"),
    change: z.enum(["added", "removed"]),
    from: z.string(),
    to: z.string(),
  }),
  z.strictObject({
    kind: z.literal("journey"),
    field: z.enum(JOURNEY_LEVEL_CHANGE_FIELDS),
    // Absent when the baseline value is too large to store.
    before: z.json().optional(),
  }),
]);

/**
 * What the agent's journey output changed relative to the journey it started from.
 * `baseline` names that starting point: the user's unsaved canvas, the saved journey, or nothing for a new journey.
 */
export const journeyChangesSchema = z.strictObject({
  baseline: z.enum(["canvas", "saved", "none"]),
  items: z.array(journeyChangeItemSchema),
  truncated: z.boolean(),
});

export type JourneyChangeItem = z.infer<typeof journeyChangeItemSchema>;
export type JourneyChanges = z.infer<typeof journeyChangesSchema>;
